import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, ChevronUp, ChevronDown,
  Music, Play, X, ExternalLink, GripVertical,
  Headphones, Eye, EyeOff, Copy, Heart, Search, Check,
} from "lucide-react";
import { useAdminAuth, LoginScreen } from "./adminAuth";
import AdminHeader from "../components/AdminHeader";
import { isYtUrl, ytId, isSpotifyUrl, isMediaUrl, embedFor } from "../lib/media";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const newSection = () => ({
  id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  name: "Neuer Anlass",
  notes: "",
  tracks: [],
});

const newTrack = () => ({
  id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  title: "",
  artist: "",
  youtube_url: "",
  note: "",
});

export default function AdminMusic() {
  const { password, authed, checking, login, logout } = useAdminAuth();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(null); // track id currently playing
  // DJ / Wishes config
  const [djPassword, setDjPassword] = useState("");
  const [showDjPw, setShowDjPw] = useState(false);
  const [wishesEnabled, setWishesEnabled] = useState(false);
  const [djSaving, setDjSaving] = useState(false);
  const [wishes, setWishes] = useState([]);
  // Bulk-paste modal: sectionIdx or null
  const [bulkSectionIdx, setBulkSectionIdx] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [music, cfg, wl] = await Promise.all([
        axios.get(`${API}/music`, { params: { password } }),
        axios.get(`${API}/dj/config`, { params: { password } }),
        axios.get(`${API}/dj/wishes`, { params: { password } }).catch(() => ({ data: [] })),
      ]);
      setSections(music.data?.sections || []);
      setDjPassword(cfg.data?.dj_password || "");
      setWishesEnabled(!!cfg.data?.wishes_enabled);
      setWishes(wl.data || []);
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (checking) return <div className="min-h-screen bg-sage-50" />;
  if (!authed) return <LoginScreen onSubmit={login} />;

  const updateSection = (idx, patch) =>
    setSections((p) => p.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const moveSection = (idx, dir) => {
    setSections((p) => {
      const next = [...p];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return p;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const removeSection = (idx) => {
    if (!window.confirm(`Anlass „${sections[idx]?.name}" mit ${sections[idx]?.tracks?.length || 0} Songs wirklich löschen?`)) return;
    setSections((p) => p.filter((_, i) => i !== idx));
  };

  const addSection = () => setSections((p) => [...p, newSection()]);

  const addTrack = (sIdx) => {
    setSections((p) =>
      p.map((s, i) => (i === sIdx ? { ...s, tracks: [...s.tracks, newTrack()] } : s))
    );
  };

  // Parse pasted text into {title, artist} rows. Supports "Artist - Title" and "Title - Artist"
  // and lines starting with numbers ("01. Song"). One song per line.
  const parseBulk = (text) =>
    text
      .split(/\r?\n/)
      .map((raw) => raw.trim())
      .filter(Boolean)
      .map((line) => {
        // Strip leading numbering like "01. ", "1) ", "- "
        const cleaned = line
          .replace(/^\s*\d+[\.\)]\s*/, "")
          .replace(/^\s*[-•*]\s*/, "")
          .trim();
        // Try to split by " - " / " – " / " — "
        for (const sep of [" - ", " – ", " — ", " | "]) {
          if (cleaned.includes(sep)) {
            const [left, right] = cleaned.split(sep, 2).map((s) => s.trim());
            if (left && right) {
              // Heuristic: if left has multiple words, guess "Artist - Title"
              return { artist: left, title: right };
            }
          }
        }
        return { artist: "", title: cleaned };
      });

  const runBulkImport = async () => {
    const rows = parseBulk(bulkText);
    if (!rows.length) {
      toast.error("Keine Zeilen erkannt");
      return;
    }
    setBulkBusy(true);
    const sIdx = bulkSectionIdx;
    // 1) Add empty rows immediately so user sees progress
    const newTracks = rows.map((r) => ({
      ...newTrack(),
      title: r.title,
      artist: r.artist,
    }));
    setSections((p) =>
      p.map((s, i) => (i === sIdx ? { ...s, tracks: [...s.tracks, ...newTracks] } : s))
    );
    setBulkSectionIdx(null);
    setBulkText("");
    toast.success(`${rows.length} Songs hinzugefügt — YouTube-Suche läuft…`);

    // 2) Sequentially look up each on YouTube (top hit)
    let found = 0;
    for (let k = 0; k < newTracks.length; k++) {
      const tr = newTracks[k];
      const query = [tr.artist, tr.title].filter(Boolean).join(" ").trim();
      if (!query) continue;
      try {
        const { data } = await axios.get(`${API}/music/search`, {
          params: { q: query, password, limit: 1 },
        });
        const hit = data?.results?.[0];
        if (hit?.youtube_url) {
          // Patch the specific track by id (position may have shifted)
          setSections((p) =>
            p.map((s, i) => {
              if (i !== sIdx) return s;
              return {
                ...s,
                tracks: s.tracks.map((t) =>
                  t.id === tr.id
                    ? {
                        ...t,
                        youtube_url: hit.youtube_url,
                        // Only overwrite empty fields
                        title: t.title || hit.title,
                        artist: t.artist || hit.artist,
                      }
                    : t
                ),
              };
            })
          );
          found += 1;
        }
      } catch (_) {
        // silent — user can search manually per row
      }
    }
    setBulkBusy(false);
    toast.success(`Fertig: ${found}/${newTracks.length} YouTube-Links gefunden. Nicht gefundene Zeilen bitte einzeln prüfen.`);
  };

  const updateTrack = (sIdx, tIdx, patch) => {
    setSections((p) =>
      p.map((s, i) => {
        if (i !== sIdx) return s;
        const tracks = s.tracks.map((t, j) => (j === tIdx ? { ...t, ...patch } : t));
        return { ...s, tracks };
      })
    );
  };

  const moveTrack = (sIdx, tIdx, dir) => {
    setSections((p) =>
      p.map((s, i) => {
        if (i !== sIdx) return s;
        const tracks = [...s.tracks];
        const j = tIdx + dir;
        if (j < 0 || j >= tracks.length) return s;
        [tracks[tIdx], tracks[j]] = [tracks[j], tracks[tIdx]];
        return { ...s, tracks };
      })
    );
  };

  const removeTrack = (sIdx, tIdx) => {
    setSections((p) =>
      p.map((s, i) => {
        if (i !== sIdx) return s;
        return { ...s, tracks: s.tracks.filter((_, j) => j !== tIdx) };
      })
    );
  };

  // Auto-lookup metadata from YouTube URL (single video or full playlist).
  // Only fills empty fields on the initial track; playlists append new tracks after it.
  const lookupUrl = async (sIdx, tIdx, url) => {
    if (!url || !url.trim()) return;
    // Only auto-fill if title is empty (avoid overwriting user's manual edits)
    const cur = sections[sIdx]?.tracks?.[tIdx];
    if (!cur || cur.title.trim()) return;
    updateTrack(sIdx, tIdx, { _looking: true });
    try {
      const res = await axios.post(
        `${API}/music/lookup`,
        { url },
        { params: { password } }
      );
      const tracks = res.data?.tracks || [];
      if (!tracks.length) throw new Error("empty");
      // First track always overrides the current row's title/artist/url
      const first = tracks[0];
      setSections((p) =>
        p.map((s, i) => {
          if (i !== sIdx) return s;
          const next = [...s.tracks];
          next[tIdx] = {
            ...next[tIdx],
            title: first.title,
            artist: first.artist,
            youtube_url: first.youtube_url,
            _looking: false,
          };
          // Playlist: append the rest as new tracks
          const isPlaylist =
            res.data?.type === "playlist" ||
            res.data?.type === "spotify_playlist" ||
            res.data?.type === "spotify_album";
          if (isPlaylist && tracks.length > 1) {
            for (let k = 1; k < tracks.length; k++) {
              next.push({
                ...newTrack(),
                title: tracks[k].title,
                artist: tracks[k].artist,
                youtube_url: tracks[k].youtube_url,
              });
            }
          }
          return { ...s, tracks: next };
        })
      );
      const isPlaylistResp =
        res.data?.type === "playlist" ||
        res.data?.type === "spotify_playlist" ||
        res.data?.type === "spotify_album";
      if (isPlaylistResp) {
        const label = res.data?.playlist_name || res.data?.album_name;
        toast.success(
          label
            ? `${tracks.length} Songs geladen: ${label}`
            : `${tracks.length} Songs aus Playlist geladen`
        );
      } else {
        toast.success("Song-Daten geladen");
      }
    } catch (err) {
      updateTrack(sIdx, tIdx, { _looking: false });
      toast.error(err?.response?.data?.detail || "Lookup fehlgeschlagen");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/music`, { sections }, { params: { password } });
      toast.success("Playlist gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const saveDjConfig = async (patch = {}) => {
    const next = {
      dj_password: patch.dj_password ?? djPassword,
      wishes_enabled: patch.wishes_enabled ?? wishesEnabled,
    };
    setDjSaving(true);
    try {
      await axios.put(`${API}/dj/config`, next, { params: { password } });
      setDjPassword(next.dj_password);
      setWishesEnabled(next.wishes_enabled);
      toast.success("DJ-Einstellung gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setDjSaving(false);
    }
  };

  const deleteWish = async (id) => {
    if (!window.confirm("Diesen Wunsch wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/wishes/${id}`, { params: { password } });
      setWishes((p) => p.filter((w) => w.id !== id));
      toast.success("Gelöscht");
    } catch {
      toast.error("Fehler");
    }
  };

  const copyDjLink = async () => {
    const url = `${window.location.origin}/dj`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("DJ-Link kopiert");
    } catch {
      toast.error("Kopieren nicht möglich — Link: " + url);
    }
  };

  const totalTracks = sections.reduce((s, sec) => s + (sec.tracks?.length || 0), 0);

  return (
    <div data-testid="admin-music" className="min-h-screen bg-sage-50 pb-32">
      <AdminHeader onLogout={logout} />

      <main className="container mx-auto px-4 md:px-12 py-10">
        {/* DJ access + wishes toggle */}
        <section
          data-testid="dj-config-panel"
          className="bg-white border border-sage-200 rounded-2xl p-5 md:p-6 mb-8"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-serif text-lg text-sage-800 inline-flex items-center gap-2">
                <Headphones size={18} /> DJ-Zugang &amp; Gäste-Wünsche
              </h3>
              <p className="text-xs text-sage-500 mt-1 max-w-xl">
                Lege ein Passwort für den DJ fest — er kann sich unter{" "}
                <code className="bg-sage-100 px-1 rounded">/dj</code> einloggen und
                sieht Wünsche + euren Ablaufplan. Wünsche gehen erst live, wenn ihr
                sie freigebt.
              </p>
            </div>
            <button
              type="button"
              data-testid="dj-copy-link"
              onClick={copyDjLink}
              className="inline-flex items-center gap-1.5 rounded-full border border-sage-300 hover:bg-sage-50 text-sage-700 px-3 py-2 text-xs uppercase tracking-wider bg-white"
            >
              <Copy size={12} /> DJ-Link kopieren
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-sage-600">
                DJ-Passwort
              </span>
              <div className="mt-2 flex gap-2">
                <div className="relative flex-1">
                  <input
                    data-testid="dj-password-input"
                    type={showDjPw ? "text" : "password"}
                    value={djPassword}
                    onChange={(e) => setDjPassword(e.target.value)}
                    placeholder="z.B. DJ2026"
                    className="w-full rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowDjPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-700"
                    aria-label={showDjPw ? "Verbergen" : "Anzeigen"}
                  >
                    {showDjPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <button
                  type="button"
                  data-testid="dj-save-pw"
                  onClick={() => saveDjConfig({ dj_password: djPassword })}
                  disabled={djSaving || !djPassword.trim()}
                  className="rounded-lg bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-4 text-xs uppercase tracking-wider"
                >
                  Sichern
                </button>
              </div>
            </label>
            <div className="flex items-center justify-between md:justify-end gap-3 md:pt-6">
              <span className="text-sm text-sage-700 inline-flex items-center gap-1.5">
                <Heart size={14} className={wishesEnabled ? "text-sage-500" : ""} />
                Gäste-Wünsche
              </span>
              <label className="cursor-pointer inline-flex items-center gap-2 select-none">
                <input
                  data-testid="wishes-toggle"
                  type="checkbox"
                  checked={wishesEnabled}
                  onChange={(e) => saveDjConfig({ wishes_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-12 h-7 bg-sage-200 peer-checked:bg-sage-500 rounded-full relative transition-colors">
                  <div
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      wishesEnabled ? "translate-x-5" : ""
                    }`}
                  />
                </div>
                <span className="text-xs text-sage-600">
                  {wishesEnabled ? "Live" : "Aus"}
                </span>
              </label>
            </div>
          </div>

          {/* Wishes list (admin can delete) */}
          {wishes.length > 0 && (
            <div className="mt-6 pt-5 border-t border-sage-100">
              <p className="text-[11px] uppercase tracking-widest text-sage-500 mb-3">
                Aktuelle Wünsche · {wishes.length}
              </p>
              <ul className="space-y-1.5 max-h-80 overflow-y-auto">
                {wishes.map((w) => (
                  <li
                    key={w.id}
                    data-testid={`admin-wish-${w.id}`}
                    className="flex items-center gap-2 rounded-lg border border-sage-100 bg-sage-50/60 px-3 py-2 text-sm"
                  >
                    <span
                      className={`shrink-0 w-8 text-center text-xs font-medium rounded-full py-0.5 ${
                        w.played
                          ? "bg-sage-200 text-sage-500"
                          : "bg-sage-500 text-white"
                      }`}
                    >
                      {w.votes}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span
                        className={`text-sage-800 truncate ${
                          w.played ? "line-through opacity-60" : ""
                        }`}
                      >
                        {w.title}
                      </span>
                      {w.artist && (
                        <span className="text-sage-500"> · {w.artist}</span>
                      )}
                      {w.guest_name && (
                        <span className="text-xs text-sage-400"> · {w.guest_name}</span>
                      )}
                      {w.played && (
                        <span className="ml-1 text-[10px] uppercase tracking-widest text-sage-400">
                          gespielt
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteWish(w.id)}
                      className="text-red-400 hover:text-red-600"
                      aria-label="Wunsch löschen"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-sage-800 inline-flex items-center gap-2">
              <Music size={24} /> Musik-Ablaufplan
            </h2>
            <p className="text-sm text-sage-600 mt-1 max-w-2xl">
              {'Lege Anlässe an (z.B. „Einzug", „Eröffnungstanz", „Party") und packe Songs mit YouTube-Link darunter. Direkt auf dieser Seite abspielbar.'}
            </p>
            <p className="mt-2 text-xs text-sage-500">
              {sections.length} Anlass{sections.length === 1 ? "" : "-Blöcke"} ·{" "}
              {totalTracks} Song{totalTracks === 1 ? "" : "s"} · nur für Admins sichtbar
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sage-400 py-16">Lädt...</div>
        ) : sections.length === 0 ? (
          <div className="bg-white border border-dashed border-sage-300 rounded-2xl p-12 text-center">
            <p className="text-sage-500 mb-4">Noch keine Anlässe angelegt.</p>
            <button
              data-testid="music-add-section-empty"
              onClick={addSection}
              className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-6 py-3 text-sm uppercase tracking-wide"
            >
              <Plus size={16} /> Ersten Anlass anlegen
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {sections.map((sec, i) => (
              <SectionCard
                key={sec.id}
                section={sec}
                index={i}
                total={sections.length}
                password={password}
                playing={playing}
                setPlaying={setPlaying}
                onUpdate={(patch) => updateSection(i, patch)}
                onMove={(dir) => moveSection(i, dir)}
                onRemove={() => removeSection(i)}
                onAddTrack={() => addTrack(i)}
                onBulkImport={() => { setBulkSectionIdx(i); setBulkText(""); }}
                onUpdateTrack={(tIdx, patch) => updateTrack(i, tIdx, patch)}
                onLookupTrack={(tIdx, url) => lookupUrl(i, tIdx, url)}
                onMoveTrack={(tIdx, dir) => moveTrack(i, tIdx, dir)}
                onRemoveTrack={(tIdx) => removeTrack(i, tIdx)}
              />
            ))}
            <div className="text-center pt-4">
              <button
                data-testid="music-add-section"
                onClick={addSection}
                className="inline-flex items-center gap-2 rounded-full border border-sage-400 text-sage-700 hover:bg-sage-100 px-6 py-3 text-sm uppercase tracking-wide bg-white"
              >
                <Plus size={16} /> Weiteren Anlass hinzufügen
              </button>
            </div>
          </div>
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-sage-200 shadow-lg z-20">
        <div className="container mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center justify-between gap-4">
          <span className="text-xs md:text-sm text-sage-600 hidden sm:inline">
            {'Änderungen erst nach „Speichern" wirksam.'}
          </span>
          <button
            data-testid="music-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-2.5 text-sm uppercase tracking-wide ml-auto"
          >
            <Save size={16} /> {saving ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </div>

      {bulkSectionIdx !== null && (
        <div
          data-testid="bulk-import-modal"
          className="fixed inset-0 bg-sage-950/50 backdrop-blur-sm z-30 flex items-center justify-center p-4"
          onClick={() => !bulkBusy && setBulkSectionIdx(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-6 py-4 border-b border-sage-200 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl text-sage-800">Songs als Text einfügen</h3>
                <p className="text-xs text-sage-500 mt-0.5">
                  {'Ein Song pro Zeile. Formate: „Interpret - Titel" oder nur „Titel". Nummerierungen (01., 1) usw.) werden automatisch entfernt.'}
                </p>
              </div>
              <button
                onClick={() => !bulkBusy && setBulkSectionIdx(null)}
                disabled={bulkBusy}
                className="text-sage-400 hover:text-sage-700 disabled:opacity-30"
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </header>
            <div className="p-6 flex-1 overflow-y-auto">
              <textarea
                data-testid="bulk-import-textarea"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                disabled={bulkBusy}
                rows={14}
                placeholder={"Beispiel:\nEd Sheeran - Perfect\nCalum Scott - You Are The Reason\nJohn Legend - All of Me\n…"}
                className="w-full rounded-lg border border-sage-300 bg-sage-50 px-4 py-3 font-mono text-sm text-sage-800 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:bg-white"
              />
              <div className="mt-2 text-xs text-sage-500">
                {bulkText.split(/\r?\n/).filter((l) => l.trim()).length} Zeile(n) erkannt.
                Für jede Zeile wird automatisch der beste YouTube-Treffer gesucht und der Link eingesetzt.
              </div>
            </div>
            <footer className="px-6 py-4 border-t border-sage-200 flex items-center justify-end gap-3 bg-sage-50">
              <button
                onClick={() => setBulkSectionIdx(null)}
                disabled={bulkBusy}
                className="rounded-full border border-sage-300 text-sage-700 hover:bg-white px-5 py-2 text-sm uppercase tracking-wide"
              >
                Abbrechen
              </button>
              <button
                data-testid="bulk-import-submit"
                onClick={runBulkImport}
                disabled={bulkBusy || !bulkText.trim()}
                className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-2 text-sm uppercase tracking-wide"
              >
                {bulkBusy ? "Sucht auf YouTube…" : "Hinzufügen & YouTube-Suche"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

const SectionCard = ({
  section, index, total, password, playing, setPlaying,
  onUpdate, onMove, onRemove,
  onAddTrack, onBulkImport, onUpdateTrack, onLookupTrack, onMoveTrack, onRemoveTrack,
}) => (
  <section
    data-testid={`music-section-${section.id}`}
    className="bg-white border border-sage-200 rounded-2xl overflow-hidden shadow-sm"
  >
    <header className="flex items-center gap-2 px-5 py-3 bg-sage-50 border-b border-sage-200">
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          className="text-sage-400 hover:text-sage-700 disabled:opacity-30"
          aria-label="Nach oben"
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          className="text-sage-400 hover:text-sage-700 disabled:opacity-30"
          aria-label="Nach unten"
        >
          <ChevronDown size={16} />
        </button>
      </div>
      <span className="font-serif text-sage-500 text-sm w-7 text-center">{index + 1}.</span>
      <input
        data-testid={`section-name-${section.id}`}
        value={section.name}
        onChange={(e) => onUpdate({ name: e.target.value })}
        className="flex-1 rounded-lg border border-sage-300 bg-white px-3 py-2 text-base font-serif text-sage-800 focus:outline-none focus:ring-2 focus:ring-sage-400"
        placeholder="Anlass-Name"
      />
      <button
        type="button"
        onClick={onRemove}
        className="rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 p-2"
        aria-label="Anlass entfernen"
      >
        <Trash2 size={16} />
      </button>
    </header>

    <div className="px-5 py-3 border-b border-sage-100">
      <input
        value={section.notes}
        onChange={(e) => onUpdate({ notes: e.target.value })}
        placeholder='Notiz (optional, z.B. „während Begrüßung leise im Hintergrund")'
        className="w-full rounded-lg border border-transparent bg-sage-50/60 hover:bg-white hover:border-sage-200 focus:bg-white focus:border-sage-300 px-3 py-2 text-sm text-sage-700 focus:outline-none focus:ring-2 focus:ring-sage-400 transition"
      />
    </div>

    {section.tracks.length === 0 ? (
      <div className="px-5 py-8 text-center">
        <p className="text-sm text-sage-400 mb-3">Noch keine Songs.</p>
        <div className="inline-flex items-center gap-2">
          <button
            data-testid={`section-add-track-${section.id}`}
            onClick={onAddTrack}
            className="inline-flex items-center gap-1.5 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 text-xs uppercase tracking-wider"
          >
            <Plus size={14} /> Song hinzufügen
          </button>
          <button
            data-testid={`section-bulk-import-${section.id}`}
            onClick={onBulkImport}
            className="inline-flex items-center gap-1.5 rounded-full border border-sage-400 text-sage-700 hover:bg-sage-100 px-4 py-2 text-xs uppercase tracking-wider bg-white"
          >
            <Copy size={14} /> Songs als Text einfügen
          </button>
        </div>
      </div>
    ) : (
      <>
        <ul className="divide-y divide-sage-100">
          {section.tracks.map((t, j) => (
            <TrackRow
              key={t.id}
              track={t}
              index={j}
              total={section.tracks.length}
              password={password}
              isPlaying={playing === t.id}
              onPlay={() => setPlaying(playing === t.id ? null : t.id)}
              onUpdate={(patch) => onUpdateTrack(j, patch)}
              onLookup={(url) => onLookupTrack(j, url)}
              onMove={(dir) => onMoveTrack(j, dir)}
              onRemove={() => onRemoveTrack(j)}
            />
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-sage-100 flex flex-wrap gap-2">
          <button
            data-testid={`section-add-track-${section.id}`}
            onClick={onAddTrack}
            className="inline-flex items-center gap-1.5 rounded-full border border-sage-300 text-sage-700 hover:bg-sage-50 px-3 py-1.5 text-xs uppercase tracking-wider bg-white"
          >
            <Plus size={12} /> Song hinzufügen
          </button>
          <button
            data-testid={`section-bulk-import-${section.id}`}
            onClick={onBulkImport}
            className="inline-flex items-center gap-1.5 rounded-full border border-sage-300 text-sage-700 hover:bg-sage-50 px-3 py-1.5 text-xs uppercase tracking-wider bg-white"
          >
            <Copy size={12} /> Songs als Text einfügen
          </button>
        </div>
      </>
    )}
  </section>
);

const TrackRow = ({ track, index, total, password, isPlaying, onPlay, onUpdate, onLookup, onMove, onRemove }) => {
  const embed = embedFor(track.youtube_url, { autoplay: true });
  const hasUrl = !!embed;
  const isSpotify = embed?.provider === "spotify";
  const busy = !!track._looking;

  // Live YouTube search state (only fires when URL is empty)
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const searchTimer = useRef(null);
  const searchSeq = useRef(0);
  const searchContainer = useRef(null);

  useEffect(() => {
    if (track.youtube_url && track.youtube_url.trim()) {
      // URL already set → suppress search
      setSuggestions([]);
      return;
    }
    const q = [track.title, track.artist].filter(Boolean).join(" ").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearching(true);
    const seq = ++searchSeq.current;
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/music/search`, {
          params: { q, password, limit: 5 },
        });
        if (seq !== searchSeq.current) return;
        setSuggestions(res.data?.results || []);
        setShowSuggestions(true);
      } catch {
        if (seq === searchSeq.current) setSuggestions([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 500);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [track.title, track.artist, track.youtube_url, password]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchContainer.current && !searchContainer.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const pickSuggestion = (r) => {
    onUpdate({ title: r.title, artist: r.artist, youtube_url: r.youtube_url });
    setShowSuggestions(false);
    setSuggestions([]);
    setPreviewId(null);
  };

  const triggerLookup = (url) => {
    if (!url || !isMediaUrl(url)) return;
    if (track.title.trim()) return; // only auto-fill when title is empty
    onLookup?.(url);
  };

  return (
    <li data-testid={`track-${track.id}`} className="px-5 py-3">
      <div className="flex items-start gap-3">
        <div className="flex flex-col pt-2">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="text-sage-400 hover:text-sage-700 disabled:opacity-30"
            aria-label="Song nach oben"
          >
            <ChevronUp size={14} />
          </button>
          <GripVertical size={12} className="text-sage-300 my-0.5" />
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="text-sage-400 hover:text-sage-700 disabled:opacity-30"
            aria-label="Song nach unten"
          >
            <ChevronDown size={14} />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="relative" ref={searchContainer}>
            <input
              data-testid={`track-title-${track.id}`}
              value={track.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Songtitel — tippe zum Suchen"
              disabled={busy}
              className="w-full rounded-lg border border-sage-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400 disabled:bg-sage-50"
            />
            {searching ? (
              <span
                data-testid={`track-searching-${track.id}`}
                className="absolute left-3 top-1/2 -translate-y-1/2 inline-block w-3 h-3 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin"
              />
            ) : (
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400 pointer-events-none"
              />
            )}

            {showSuggestions && suggestions.length > 0 && !track.youtube_url?.trim() && (
              <div
                data-testid={`track-suggestions-${track.id}`}
                className="absolute z-30 left-0 right-0 mt-1 bg-white border border-sage-200 rounded-xl shadow-lg overflow-hidden max-h-96 overflow-y-auto min-w-[380px]"
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-sage-100 bg-sage-50/60">
                  <span className="text-[10px] uppercase tracking-widest text-sage-500">
                    YouTube-Treffer · anhören &amp; auswählen
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(false)}
                    className="text-sage-400 hover:text-sage-700"
                    aria-label="Schließen"
                  >
                    <X size={14} />
                  </button>
                </div>
                <ul>
                  {suggestions.map((r) => {
                    const isPreview = previewId === r.video_id;
                    return (
                      <li
                        key={r.video_id}
                        data-testid={`track-suggestion-${r.video_id}`}
                        className="border-b border-sage-100 last:border-0"
                      >
                        <div className="flex items-start gap-3 px-3 py-2">
                          <img
                            src={`https://i.ytimg.com/vi/${r.video_id}/mqdefault.jpg`}
                            alt=""
                            className="w-16 h-11 object-cover rounded-md shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sage-800 text-sm truncate">
                              {r.title}
                            </div>
                            <div className="text-xs text-sage-500 truncate">
                              {r.artist}
                            </div>
                            <div className="mt-1.5 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setPreviewId(isPreview ? null : r.video_id)}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide ${
                                  isPreview ? "bg-sage-600 text-white" : "bg-sage-100 text-sage-700 hover:bg-sage-200"
                                }`}
                              >
                                {isPreview ? <X size={11} /> : <Play size={11} />}
                                {isPreview ? "Schließen" : "Anhören"}
                              </button>
                              <button
                                type="button"
                                data-testid={`track-pick-${r.video_id}`}
                                onClick={() => pickSuggestion(r)}
                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide bg-sage-500 hover:bg-sage-600 text-white"
                              >
                                <Check size={11} /> Wählen
                              </button>
                            </div>
                          </div>
                        </div>
                        {isPreview && (
                          <div className="px-3 pb-3">
                            <div className="rounded-lg overflow-hidden bg-black aspect-video">
                              <iframe
                                title={`Preview ${r.title}`}
                                src={`https://www.youtube-nocookie.com/embed/${r.video_id}?autoplay=1&rel=0&modestbranding=1`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <input
            value={track.artist}
            onChange={(e) => onUpdate({ artist: e.target.value })}
            placeholder="Künstler"
            disabled={busy}
            className="rounded-lg border border-sage-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400 disabled:bg-sage-50"
          />
          <div className="md:col-span-2 relative">
            <input
              data-testid={`track-url-${track.id}`}
              value={track.youtube_url}
              onChange={(e) => onUpdate({ youtube_url: e.target.value })}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text");
                if (pasted) {
                  // schedule after React updates state
                  setTimeout(() => triggerLookup(pasted), 100);
                }
              }}
              onBlur={(e) => triggerLookup(e.target.value)}
              placeholder="YouTube oder Spotify Link (Video/Track oder Playlist) — Titel/Künstler laden automatisch"
              disabled={busy}
              className={`w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-sage-50 ${
                track.youtube_url && !isMediaUrl(track.youtube_url)
                  ? "border-red-300 focus:ring-red-300"
                  : "border-sage-200 focus:ring-sage-400"
              }`}
            />
            {busy && (
              <div
                data-testid={`track-loading-${track.id}`}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-500 text-xs inline-flex items-center gap-1"
              >
                <span className="inline-block w-3 h-3 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin" />
                lädt...
              </div>
            )}
          </div>
          <input
            value={track.note}
            onChange={(e) => onUpdate({ note: e.target.value })}
            placeholder="Notiz (optional)"
            className="md:col-span-2 rounded-lg border border-transparent bg-sage-50/60 hover:bg-white hover:border-sage-200 focus:bg-white focus:border-sage-300 px-3 py-2 text-xs text-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-400 transition"
          />
        </div>

        <div className="flex flex-col gap-1 items-end shrink-0">
          {hasUrl && (
            <button
              type="button"
              data-testid={`track-play-${track.id}`}
              onClick={onPlay}
              className={`rounded-full p-2 transition-colors ${
                isPlaying
                  ? "bg-sage-600 text-white"
                  : "bg-sage-500 hover:bg-sage-600 text-white"
              }`}
              aria-label={isPlaying ? "Player schließen" : "Abspielen"}
            >
              {isPlaying ? <X size={14} /> : <Play size={14} />}
            </button>
          )}
          {hasUrl && (
            <a
              href={track.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full p-2 text-sage-400 hover:text-sage-700"
              aria-label={isSpotify ? "In Spotify öffnen" : "In YouTube öffnen"}
            >
              <ExternalLink size={14} />
            </a>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="rounded-full p-2 text-red-400 hover:text-red-700"
            aria-label="Song entfernen"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {isPlaying && embed && (
        <div
          data-testid={`track-player-${track.id}`}
          className={`mt-3 rounded-xl overflow-hidden border border-sage-200 max-w-xl mx-auto ${
            isSpotify ? "bg-black/5" : "bg-black aspect-video"
          }`}
        >
          <iframe
            title={`${embed.provider} ${track.title || ""}`}
            src={embed.src}
            className="w-full"
            style={embed.height ? { height: embed.height } : undefined}
            height={embed.height || undefined}
            frameBorder="0"
            allow="autoplay; encrypted-media; clipboard-write; fullscreen"
            allowFullScreen
          />
        </div>
      )}
    </li>
  );
};
